import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenantTimeFormat } from '../../../stores/tenant.store';
import { wallTime, type CoreCourseFacts } from '../util/classroom';

// SIN AULA, LO QUE SABE EL NÚCLEO DEL CURSO: sección, período, horario,
// profesor y la nota final cuando el acta está cerrada (util/classroom).

/** Claves literales (el guardián de i18n busca cada clave escrita entera). */
const WEEKDAY_KEYS = [
  'portal.syllabus.weekdays.sunday',
  'portal.syllabus.weekdays.monday',
  'portal.syllabus.weekdays.tuesday',
  'portal.syllabus.weekdays.wednesday',
  'portal.syllabus.weekdays.thursday',
  'portal.syllabus.weekdays.friday',
  'portal.syllabus.weekdays.saturday',
] as const;

function Fact({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="#8b949e" display="block">
        {label}
      </Typography>
      <Box sx={{ color: '#e6edf3' }}>{children}</Box>
    </Box>
  );
}

export function CourseCoreFacts({ facts }: { facts: CoreCourseFacts }): JSX.Element {
  const { t } = useTranslation('sis');
  const timeFmt = useTenantTimeFormat();
  return (
    <Box
      sx={{
        bgcolor: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 2,
        p: 2,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
      }}
    >
      <Fact label={t('portal.courseShell.core.section')}>
        <Typography variant="body2" fontWeight={600}>
          {facts.sectionCode ?? '—'}
        </Typography>
        {facts.termName && (
          <Typography variant="caption" color="#8b949e">
            {facts.termName}
          </Typography>
        )}
      </Fact>
      <Fact label={t('portal.courseShell.core.schedule')}>
        {facts.schedule.length === 0 ? (
          <Typography variant="body2" color="#8b949e">
            {t('portal.courseShell.core.noSchedule')}
          </Typography>
        ) : (
          facts.schedule.map((s, i) => (
            <Typography key={i} variant="body2">
              {/* Un horario importado de LABTS trae solo el inicio (término =
                  inicio): se muestra el inicio, no un «7:00 PM–7:00 PM». */}
              {s.endTime && s.endTime !== s.startTime
                ? t('portal.courseShell.core.scheduleLine', {
                    day: t(WEEKDAY_KEYS[s.dayOfWeek] ?? WEEKDAY_KEYS[0]),
                    start: wallTime(s.startTime, timeFmt),
                    end: wallTime(s.endTime, timeFmt),
                  })
                : t('portal.courseShell.core.scheduleLineStartOnly', {
                    day: t(WEEKDAY_KEYS[s.dayOfWeek] ?? WEEKDAY_KEYS[0]),
                    start: wallTime(s.startTime, timeFmt),
                  })}
              {s.location ? ` · ${s.location}` : ''}
            </Typography>
          ))
        )}
      </Fact>
      <Fact label={t('portal.courseShell.core.faculty')}>
        <Typography variant="body2">{facts.facultyName ?? '—'}</Typography>
      </Fact>
      <Fact label={t('portal.courseShell.core.finalGrade')}>
        {facts.finalGrade ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1" fontWeight={700}>
              {facts.finalGrade.label}
            </Typography>
            {facts.finalGrade.status && (
              <Chip
                size="small"
                variant="outlined"
                color={facts.finalGrade.status === 'approved' ? 'success' : 'error'}
                label={
                  facts.finalGrade.status === 'approved'
                    ? t('portal.courseShell.core.approved')
                    : t('portal.courseShell.core.failed')
                }
              />
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="#8b949e">
            {t('portal.courseShell.core.finalPending')}
          </Typography>
        )}
      </Fact>
    </Box>
  );
}
